/** Guided onboarding wizard — teaches the correct SilentPoster setup order. */

import { motion } from "framer-motion";
import { ArrowLeft, Bot, CheckCircle2, ChevronRight, Crown, MessageSquare, Play, Radio, ShieldCheck, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button, Card } from "@/design";
import { useDiagnostics } from "@/api/growth";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

const steps = [
  {
    key: "subscription",
    title: "Активируйте доступ",
    description: "Триал, тариф или бонусные дни — без активного доступа воркеры не стартуют.",
    url: "/app/billing",
    icon: Crown,
    okCodes: ["subscription_active"],
    badCodes: ["subscription_inactive"],
  },
  {
    key: "accounts",
    title: "Подключите Telegram-аккаунт",
    description: "Это аккаунт, от имени которого сервис будет публиковать объявления.",
    url: "/app/accounts",
    icon: Users,
    okCodes: ["accounts_ready"],
    badCodes: ["no_accounts", "no_enabled_accounts"],
  },
  {
    key: "chats",
    title: "Добавьте чаты",
    description: "Импортируйте ссылки, отсортируйте список и выключите лишние чаты.",
    url: "/app/chats",
    icon: MessageSquare,
    okCodes: ["chats_ready"],
    badCodes: ["no_chats"],
  },
  {
    key: "templates",
    title: "Создайте шаблон",
    description: "Подготовьте текст объявления, медиа, эмодзи и spintax-варианты.",
    url: "/app/templates",
    icon: Bot,
    okCodes: ["templates_ready"],
    badCodes: ["no_templates", "no_active_template"],
  },
  {
    key: "mailing",
    title: "Включите рассылку",
    description: "Выберите темп и safety-профиль, затем запустите автопостинг.",
    url: "/app/mailing",
    icon: Radio,
    okCodes: ["mailing_enabled"],
    badCodes: ["mailing_paused"],
  },
  {
    key: "diagnostics",
    title: "Проверьте систему",
    description: "Smart Diagnostics покажет, что мешает запуску и куда нажать дальше.",
    url: "/app/diagnostics",
    icon: ShieldCheck,
    okCodes: [],
    badCodes: [],
  },
] as const;

export function OnboardingPage() {
  const navigate = useNavigate();
  const { data } = useDiagnostics();
  const codes = new Set((data?.items ?? []).map((item) => item.code));
  const done = steps.filter((step) => step.okCodes.some((code) => codes.has(code))).length;
  const percent = Math.round((done / Math.max(1, steps.length - 1)) * 100);
  const nextStep = steps.find((step) => step.badCodes.some((code) => codes.has(code))) ?? steps[steps.length - 1];

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate(-1);
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight">Быстрый старт</div>
        <div className="size-10" />
      </div>

      <Card padded={false} className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/25 via-transparent to-transparent" aria-hidden />
        <div className="relative p-6 space-y-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-medium text-text-muted">
            <Play className="size-4 text-accent fill-accent" />
            Wizard
          </div>
          <div>
            <div className="text-[30px] leading-tight font-semibold tracking-tight">
              Настроим рассылку по шагам
            </div>
            <div className="text-sm text-text-muted mt-2 leading-relaxed">
              SilentPoster мощный, но его важно запускать в правильном порядке. Этот мастер ведёт от нуля до первой безопасной рассылки.
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-xs text-text-muted mb-2">
              <span>Прогресс</span>
              <span>{Math.min(percent, 100)}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
              <div className="h-full rounded-full bg-gradient-accent" style={{ width: `${Math.min(percent, 100)}%` }} />
            </div>
          </div>
          <Button fullWidth onClick={() => navigate(nextStep.url)}>
            Продолжить: {nextStep.title}
          </Button>
        </div>
      </Card>

      <div className="space-y-2">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1">
          Шаги настройки
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
          {steps.map((step, index) => {
            const isDone = step.okCodes.some((code) => codes.has(code));
            const hasProblem = step.badCodes.some((code) => codes.has(code));
            const Icon = step.icon;
            return (
              <motion.button
                key={step.key}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.25 }}
                onClick={() => navigate(step.url)}
                className="w-full px-4 py-3.5 flex items-start gap-3 text-left hover:bg-white/[0.03] transition-colors"
              >
                <div
                  className={cn(
                    "size-9 rounded-xl grid place-items-center shrink-0",
                    isDone ? "bg-success/15 text-success" : hasProblem ? "bg-accent-soft text-accent" : "bg-white/[0.04] text-text-dim",
                  )}
                >
                  {isDone ? <CheckCircle2 className="size-4" /> : <Icon className="size-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium">{step.title}</div>
                  <div className="text-[11px] text-text-muted leading-relaxed mt-0.5">{step.description}</div>
                </div>
                <ChevronRight className="size-4 text-text-dim mt-2 shrink-0" />
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
